import { useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { cityOfLabel } from "@/lib/address-parse";
import { cn } from "@/lib/utils";
import zipList from "@/lib/ziplist";

type Props = {
  id?: string;
  city: string;
  postalCode: string;
  /** Poziva se s nazivom grada (bez poštanskog broja) i poštanskim brojem iz ziplist-a. */
  onSelect: (city: string, postalCode: string) => void;
  className?: string;
};

/**
 * Odabir grada iz ziplist-a s pretragom — isti obrazac koji koristi forma
 * maloprodajne narudžbe. Odabir popunjava i poštanski broj, koji ostaje
 * ručno izmjenjiv u zasebnom polju.
 */
export function CityCombobox({ id, city, postalCode, onSelect, className }: Props) {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("h-8 w-full justify-between text-xs font-normal", className)}
        >
          <span className="truncate">{city || "Odaberi grad…"}</span>
          <ChevronsUpDown className="ml-auto h-3 w-3 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput placeholder="Pretraži grad…" className="h-8 text-xs" />
          <CommandList>
            <CommandEmpty className="py-3 text-xs">Nema rezultata</CommandEmpty>
            <CommandGroup>
              {zipList.map((entry) => (
                <CommandItem
                  key={entry.label}
                  value={entry.label}
                  onSelect={() => {
                    onSelect(cityOfLabel(entry.label), entry.value);
                    setOpen(false);
                  }}
                  className="text-xs"
                >
                  <Check
                    className={cn(
                      "mr-1.5 h-3 w-3",
                      city && cityOfLabel(entry.label) === city && entry.value === postalCode
                        ? "opacity-100"
                        : "opacity-0",
                    )}
                  />
                  {entry.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
